"use client";

import React from "react";
import EditSection, { TextEditor } from "../../sections/shared/EditSection";
import { SectionContentProps } from "../../types";
import { Input, Textarea } from "rizzui";
import { PiPlusBold, PiTrash, PiUploadSimple } from "react-icons/pi";
import homepageApi from "@/services/homepageApi";
import toast from "react-hot-toast";

export default function UniversityProgramsEdit({
  content,
  onContentChange,
}: SectionContentProps) {
  const updateContent = (key: string, value: any) => {
    onContentChange?.({ ...content, [key]: value });
  };

  const programs = content.programs || [
    { title: "Graduation Program", description: "Embark on a journey of knowledge discovery, and growth at our university.", link: "#" },
    { title: "Summer Program", description: "Embark on a journey of knowledge discovery, and growth at our university.", link: "#" },
    { title: "Undergraduate", description: "Embark on a journey of knowledge discovery, and growth at our university.", link: "#" },
    { title: "Online Program", description: "Embark on a journey of knowledge discovery, and growth at our university.", link: "#" },
  ];

  return (
    <EditSection
      content={content}
      onContentChange={onContentChange || (() => {})}
      contentFields={
        <div className="space-y-4">
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Section Headers</h4>
            <TextEditor
              label="Title"
              value={content.title}
              onChange={(v) => updateContent("title", v)}
              placeholder="Our Academic Programs"
            />
            <TextEditor
              label="Subtitle"
              value={content.subtitle}
              onChange={(v) => updateContent("subtitle", v)}
              placeholder="Explore our wide range of undergraduate and graduate programs..."
            />
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5 block">
                View All Link Text
              </label>
              <Input
                value={content.viewAllText || ""}
                onChange={(e) => updateContent("viewAllText", e.target.value)}
                placeholder="more program"
                className="!rounded-lg"
              />
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-bold text-gray-900">Programs</h4>
              <button
                onClick={() => {
                  const newProgram = {
                    title: "New Program",
                    description: "Embark on a journey of knowledge discovery, and growth at our university.",
                    link: "#",
                  };
                  updateContent("programs", [...programs, newProgram]);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition-colors"
              >
                <PiPlusBold className="w-3 h-3" />
                Add Program
              </button>
            </div>

            <div className="space-y-3">
              {programs.map((program: any, idx: number) => (
                <div key={idx} className="bg-white rounded-lg border border-gray-200 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-gray-400 uppercase">
                      Program {idx + 1}
                    </span>
                    <button
                      onClick={() => {
                        const updated = programs.filter((_: any, i: number) => i !== idx);
                        updateContent("programs", updated);
                      }}
                      className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                    >
                      <PiTrash className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Image Upload Area */}
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1 block">
                      Background Image
                    </label>
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-10 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0 relative group/img">
                        {program.imageUrl ? (
                          <img
                            src={program.imageUrl}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            🖼️
                          </div>
                        )}
                        <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity cursor-pointer">
                          <PiUploadSimple className="w-4 h-4 text-white" />
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > 2 * 1024 * 1024) {
                                toast.error("Image must be under 2MB");
                                return;
                              }
                              const uploadPromise = homepageApi.uploadImage(file);
                              toast.promise(uploadPromise, {
                                loading: "Uploading image...",
                                success: "Image uploaded!",
                                error: "Failed to upload",
                              });
                              const response = await uploadPromise;
                              if (response.success && response.data?.url) {
                                const updated = [...programs];
                                updated[idx] = { ...program, imageUrl: response.data.url };
                                updateContent("programs", updated);
                              }
                            }}
                          />
                        </label>
                      </div>
                      <Input
                        value={program.imageUrl || ""}
                        onChange={(e) => {
                          const updated = [...programs];
                          updated[idx] = { ...program, imageUrl: e.target.value };
                          updateContent("programs", updated);
                        }}
                        placeholder="Or paste image URL"
                        className="!rounded-lg text-xs flex-1 h-8"
                      />
                    </div>
                  </div>

                  <Input
                    value={program.title || ""}
                    onChange={(e) => {
                      const updated = [...programs];
                      updated[idx] = { ...program, title: e.target.value };
                      updateContent("programs", updated);
                    }}
                    placeholder="Program Title"
                    className="!rounded text-xs"
                  />

                  <Textarea
                    value={program.description || ""}
                    onChange={(e) => {
                      const updated = [...programs];
                      updated[idx] = { ...program, description: e.target.value };
                      updateContent("programs", updated);
                    }}
                    placeholder="Program Description"
                    className="!rounded text-xs"
                    rows={2}
                  />

                  <Input
                    value={program.link || ""}
                    onChange={(e) => {
                      const updated = [...programs];
                      updated[idx] = { ...program, link: e.target.value };
                      updateContent("programs", updated);
                    }}
                    placeholder="Link URL"
                    className="!rounded text-xs"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      }
    />
  );
}
